import { useEffect, useRef, useState } from "react";
import { Link } from "react-router";
import { ArrowLeft, MoreHorizontal } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";

import { VoltApiError } from "../voltApi";
import { DraftMarkdown } from "./DraftMarkdown";
import {
  buddyFor,
  downloadName,
  draftTitle,
  formatDateTime,
  isRunning,
  lightTone,
  normalizeCommittee,
  removeSection,
  sourceText,
  trafficLightReason,
} from "./draftText";
import { RestoreDialog } from "./RestoreDialog";
import { RevisionPanel } from "./RevisionPanel";
import { TrafficLightDot } from "./TrafficLight";
import { VersionCompare } from "./VersionCompare";
import { WorkshopError } from "./WorkshopError";
import type { WorkshopMeta } from "./WorkshopFrame";
import { errorText, workshopApi } from "./workshopApi";
import type { Draft, SlackTakeover, Version } from "./types";

const lightBox = {
  success: "bg-success-soft",
  destructive: "bg-destructive-soft",
  warning: "bg-warning-soft",
} as const;

/** A draft: while running, the research ticker; when done, the text to read, edit, copy and download. */
export const DraftView = ({
  draft,
  meta,
  onUpdated,
  onDeleted,
}: {
  draft: Draft;
  meta: WorkshopMeta;
  onUpdated: (d: Draft) => void;
  onDeleted: () => void;
}) => {
  const { status, committees } = meta;
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(draft.entwurf ?? "");
  // Unsaved edits survive the preview; only saving or discarding drops them.
  const [dirty, setDirty] = useState(false);
  const [saved, setSaved] = useState(false);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [compare, setCompare] = useState<Version | null>(null);
  const [restoreNr, setRestoreNr] = useState<number | null>(null);
  const [slackBusy, setSlackBusy] = useState(false);
  const [slackResult, setSlackResult] = useState<SlackTakeover | null>(null);
  const [deadlineOpen, setDeadlineOpen] = useState(false);
  const [deadline, setDeadline] = useState("");
  const [deadlineHint, setDeadlineHint] = useState<string | null>(null);

  const running = isRunning(draft);
  const [gone, setGone] = useState(false);
  const reason = trafficLightReason(draft.bewertung);
  // Light and sources have boxes of their own, so both leave the assessment text.
  const assessment = removeSection(
    removeSection(draft.bewertung ?? "", "Ampel"),
    "Quellen",
  );

  // Polls every 3 s; only the newest answer counts, a slow older one is dropped.
  const pollSeq = useRef(0);
  useEffect(() => {
    if (!running) return;
    const counter = pollSeq;
    const t = setInterval(async () => {
      const seq = ++counter.current;
      try {
        const fresh = await workshopApi.draft(draft.id);
        if (seq === counter.current) onUpdated(fresh);
      } catch (e) {
        // A deleted draft stays gone; anything else is retried on the next tick.
        if (e instanceof VoltApiError && e.status === 404) {
          clearInterval(t);
          setGone(true);
        }
      }
    }, 3000);
    return () => {
      clearInterval(t);
      counter.current++;
    };
  }, [running, draft.id, onUpdated]);

  useEffect(() => {
    if (!dirty) setText(draft.entwurf ?? "");
  }, [draft.entwurf, dirty]);

  useEffect(() => {
    setCompare(null);
  }, [draft.id, draft.aktuelle_nr]);

  const flashSaved = () => {
    setSaved(true);
    setTimeout(() => setSaved(false), 2500);
  };

  const openCompare = async (v: Version | null) => {
    try {
      setCompare(
        v && v.text === undefined
          ? await workshopApi.version(draft.id, v.nr)
          : v,
      );
    } catch (e) {
      setError(errorText(e));
    }
  };

  const restore = async (nr: number) => {
    if (!draft.aktuelle_nr) return;
    try {
      const r = await workshopApi.restore(draft.id, nr, draft.aktuelle_nr);
      onUpdated(r.entwurf);
      setCompare(null);
      setError(null);
    } catch (e) {
      setError(errorText(e));
      workshopApi
        .draft(draft.id)
        .then(onUpdated)
        .catch(() => {});
    }
  };

  const save = async () => {
    try {
      onUpdated(await workshopApi.save(draft.id, { entwurf: text }));
      setDirty(false);
      setEditing(false);
      flashSaved();
    } catch (e) {
      setError(errorText(e));
    }
  };

  const discard = () => {
    setText(draft.entwurf ?? "");
    setDirty(false);
    setEditing(false);
  };

  // Only the newest committee choice counts, as with polling.
  const committeeSeq = useRef(0);
  const saveCommittee = async (gremium: string) => {
    const seq = ++committeeSeq.current;
    try {
      const saved = await workshopApi.save(draft.id, { gremium });
      if (seq !== committeeSeq.current) return;
      onUpdated(saved);
      flashSaved();
    } catch (e) {
      if (seq === committeeSeq.current) setError(errorText(e));
    }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(draft.entwurf ?? "");
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      setError(errorText(e));
    }
  };

  const download = () => {
    const blob = new Blob([draft.entwurf ?? ""], {
      type: "text/markdown;charset=utf-8",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = downloadName(draft);
    a.click();
    URL.revokeObjectURL(url);
  };

  // Author, Fraktion view or a member of the draft's committee; same rule as the server.
  const maySlack =
    !!status &&
    (status.admin_sicht ||
      draft.user_id === status.user_id ||
      (!!draft.gremium &&
        status.gremien.some(
          (g) => normalizeCommittee(g) === normalizeCommittee(draft.gremium!),
        )));
  const slackRow =
    draft.slack_record_id && status
      ? status.slack_zeile_url + draft.slack_record_id
      : null;

  // The deadline is asked before creating; prefilled with the committee's next session when known.
  const askDeadline = async () => {
    setError(null);
    setDeadlineOpen(true);
    setDeadline("");
    setDeadlineHint("Nächste Sitzung wird gesucht …");
    const unknown = "Keine nächste Sitzung bekannt, bitte ein Datum eintragen.";
    try {
      const v = await workshopApi.slackDeadline(draft.id);
      if (v.datum) {
        setDeadline(v.datum);
        setDeadlineHint(
          `Vorbelegt mit der nächsten Sitzung (${v.gremium ?? draft.gremium}).`,
        );
      } else setDeadlineHint(unknown);
    } catch {
      setDeadlineHint(unknown);
    }
  };

  const takeOverToSlack = async () => {
    if (!deadline) return;
    setSlackBusy(true);
    setError(null);
    try {
      const r = await workshopApi.slackTakeover(draft.id, deadline);
      setSlackResult(r);
      setDeadlineOpen(false);
      onUpdated({
        ...draft,
        slack_record_id: r.record_id,
        slack_uebernommen_am:
          draft.slack_uebernommen_am ?? new Date().toISOString(),
      });
    } catch (e) {
      setError(errorText(e));
    } finally {
      setSlackBusy(false);
    }
  };

  const restart = async () => {
    try {
      onUpdated(await workshopApi.restart(draft.id));
    } catch (e) {
      setError(errorText(e));
    }
  };

  const remove = async () => {
    try {
      await workshopApi.remove(draft.id);
      onDeleted();
    } catch (e) {
      setError(errorText(e));
    }
  };

  const committeeKnown = committees.some((c) => c.gremium === draft.gremium);

  return (
    <section className="flex flex-col gap-5">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <Link
            to="/workshop"
            className="inline-flex items-center gap-1 text-sm text-primary hover:underline"
          >
            <ArrowLeft className="size-4" />
            Alle Entwürfe
          </Link>
          <h1 className="mt-1 text-2xl font-bold tracking-tight text-balance">
            {draftTitle(draft)}
          </h1>
        </div>
        {!running && (
          <DropdownMenu onOpenChange={() => setConfirmDelete(false)}>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" aria-label="Weitere Aktionen">
                <MoreHorizontal />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              {/* Two clicks instead of a dialog: in the Fraktion view you would
                  otherwise delete someone else's draft by accident. */}
              <DropdownMenuItem
                variant="destructive"
                onSelect={(e) => {
                  if (confirmDelete) remove();
                  else {
                    e.preventDefault();
                    setConfirmDelete(true);
                  }
                }}
              >
                {confirmDelete ? "Wirklich löschen?" : "Löschen"}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>

      {error && <WorkshopError>{error}</WorkshopError>}

      {running && gone && (
        <WorkshopError>
          Dieser Entwurf wurde gelöscht.{" "}
          <Link to="/workshop" className="underline">
            Alle Entwürfe
          </Link>
        </WorkshopError>
      )}

      {running && !gone && (
        <Card>
          <CardContent className="flex flex-col gap-3">
            <div className="flex items-center gap-3">
              <span className="size-2.5 animate-pulse rounded-full bg-primary" />
              <span className="text-sm font-medium">
                {draft.status === "wartet"
                  ? "Wird gestartet …"
                  : "Recherche läuft"}
              </span>
            </div>
            {/* Newest message on top, so the current state stays in view. */}
            <ol className="flex flex-col gap-1.5">
              {[...draft.verlauf].reverse().map((v, i) => (
                <li
                  key={i}
                  className="flex gap-3 text-sm text-muted-foreground"
                >
                  <span className="shrink-0 pt-0.5 text-xs tabular-nums">
                    {new Date(v.t).toLocaleTimeString("de-DE", {
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </span>
                  <span>{v.text}</span>
                </li>
              ))}
            </ol>
            <p className="text-xs text-muted-foreground">
              Die Werkstatt liest Drucksachen, sucht im Netz und prüft die
              Volt-Position. Das dauert ein paar Minuten – die Seite
              aktualisiert sich selbst.
            </p>
          </CardContent>
        </Card>
      )}

      {draft.status === "fehler" && (
        <div className="flex flex-col gap-3 rounded-xl bg-destructive-soft p-5">
          <div>
            <p className="text-sm font-medium text-destructive">
              Der Lauf ist fehlgeschlagen
            </p>
            <p className="mt-1 text-sm text-muted-foreground">{draft.fehler}</p>
          </div>
          <div>
            <Button onClick={restart}>Erneut versuchen</Button>
          </div>
          <p className="text-xs text-muted-foreground">
            Thema, Hinweise und Anhänge werden übernommen; das zählt nicht gegen
            dein Kontingent.
          </p>
        </div>
      )}

      {draft.status === "fertig" && draft.entwurf && (
        // Revision column on the left, the draft on the right; stacked on mobile.
        <div className="grid items-start gap-6 lg:grid-cols-[minmax(0,22rem)_minmax(0,1fr)]">
          <aside className="order-2 lg:sticky lg:top-6 lg:order-1">
            <RevisionPanel
              draft={draft}
              locked={editing}
              unsaved={dirty}
              selectedNr={compare?.nr ?? null}
              meta={meta}
              onUpdated={onUpdated}
              onCompare={openCompare}
              onRestore={async (nr) => setRestoreNr(nr)}
            />
          </aside>
          <div className="order-1 flex min-w-0 flex-col gap-5 lg:order-2">
            <div className="flex flex-wrap items-center gap-2">
              <Button onClick={copy} disabled={dirty}>
                {copied ? "Kopiert" : "Entwurf kopieren"}
              </Button>
              <Button variant="outline" onClick={download} disabled={dirty}>
                Als Datei speichern
              </Button>
              <Button variant="outline" onClick={() => setEditing((b) => !b)}>
                {editing ? "Vorschau" : "Bearbeiten"}
              </Button>
              {maySlack &&
                (slackRow ? (
                  <Button variant="outline" asChild>
                    <a href={slackRow} target="_blank" rel="noreferrer">
                      In Slack ansehen
                    </a>
                  </Button>
                ) : (
                  <Button
                    variant="outline"
                    onClick={askDeadline}
                    disabled={
                      !status?.slack_listen ||
                      slackBusy ||
                      editing ||
                      dirty ||
                      deadlineOpen
                    }
                    title={
                      status?.slack_listen
                        ? "Legt einen Eintrag in der Slack-Liste „Anträge & Anfragen“ an"
                        : "Die Slack-Übernahme ist auf dem Server gerade nicht eingerichtet"
                    }
                  >
                    In Slack übernehmen
                  </Button>
                ))}
              {saved && (
                <span className="text-sm text-success">Gespeichert</span>
              )}
            </div>

            {deadlineOpen && !slackRow && (
              <div className="flex flex-col gap-2 rounded-xl border bg-card p-4 text-sm">
                <div className="flex flex-wrap items-center gap-3">
                  <label htmlFor="workshop-deadline" className="font-medium">
                    Frist
                  </label>
                  <Input
                    id="workshop-deadline"
                    type="date"
                    value={deadline}
                    onChange={(e) => setDeadline(e.target.value)}
                    className="w-auto"
                  />
                  <Button
                    onClick={takeOverToSlack}
                    disabled={!deadline || slackBusy || dirty}
                  >
                    {slackBusy ? "Wird übernommen …" : "Eintrag anlegen"}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => setDeadlineOpen(false)}
                    disabled={slackBusy}
                  >
                    Abbrechen
                  </Button>
                </div>
                {deadlineHint && (
                  <p className="text-xs text-muted-foreground">
                    {deadlineHint}
                  </p>
                )}
              </div>
            )}

            {slackResult && (
              <div className="flex flex-col gap-1 rounded-xl bg-success-soft p-4 text-sm">
                <p className="font-medium text-success">
                  {slackResult.vorhanden
                    ? "Dieser Entwurf steht schon in der Antragsliste."
                    : "In die Antragsliste „Anträge & Anfragen“ übernommen."}{" "}
                  <a
                    href={slackResult.url}
                    target="_blank"
                    rel="noreferrer"
                    className="underline"
                  >
                    Eintrag in Slack öffnen
                  </a>
                </p>
                {slackResult.hinweise.map((h) => (
                  <p key={h} className="text-warning-text">
                    {h}
                  </p>
                ))}
                {!slackResult.vorhanden && (
                  <p className="text-muted-foreground">
                    Ausschuss-Kanal bitte in Slack ergänzen.
                  </p>
                )}
              </div>
            )}

            {draft.ampel && (
              <div
                className={cn(
                  "rounded-xl p-5",
                  lightBox[lightTone(draft.ampel)],
                )}
              >
                <div className="flex items-center gap-3">
                  <TrafficLightDot light={draft.ampel} className="size-3" />
                  <span className="text-sm font-semibold">
                    {draft.ampel}
                    {draft.score !== null
                      ? ` · Erfolgsaussicht ${draft.score} %`
                      : ""}
                  </span>
                </div>
                {reason && <p className="mt-2 text-sm">{reason}</p>}
              </div>
            )}

            {/* The committee controls who may collaborate; it is changed only in edit mode. */}
            <div className="flex flex-wrap items-center gap-x-6 gap-y-2 rounded-xl border bg-card px-4 py-3 text-sm text-muted-foreground">
              <span>
                Art: <span className="text-foreground">{draft.art}</span>
              </span>
              <span className="flex items-center gap-2">
                Gremium:
                {editing ? (
                  <Select
                    value={draft.gremium ?? undefined}
                    onValueChange={saveCommittee}
                  >
                    <SelectTrigger
                      size="sm"
                      aria-label="Gremium"
                      className="text-foreground"
                    >
                      <SelectValue placeholder="Bitte wählen …" />
                    </SelectTrigger>
                    <SelectContent>
                      {committees.map((c) => (
                        <SelectItem key={c.gremium} value={c.gremium}>
                          {c.gremium}
                        </SelectItem>
                      ))}
                      {draft.gremium && !committeeKnown && (
                        <SelectItem value={draft.gremium}>
                          {draft.gremium}
                        </SelectItem>
                      )}
                    </SelectContent>
                  </Select>
                ) : (
                  <span className="text-foreground">
                    {draft.gremium ?? "—"}
                  </span>
                )}
              </span>
              <span>
                Buddy:{" "}
                <span className="text-foreground">
                  {buddyFor(committees, draft.gremium)}
                </span>
              </span>
              {draft.ersteller && (
                <span>
                  Erstellt von:{" "}
                  <span className="text-foreground">{draft.ersteller}</span>
                </span>
              )}
              <span>
                Erstellt:{" "}
                <span className="tabular-nums text-foreground">
                  {formatDateTime(draft.erstellt_am)}
                </span>
              </span>
              {draft.geaendert_am &&
                draft.geaendert_am !== draft.erstellt_am && (
                  <span>
                    Geändert:{" "}
                    <span className="tabular-nums text-foreground">
                      {formatDateTime(draft.geaendert_am)}
                    </span>
                  </span>
                )}
            </div>

            {!editing && dirty && (
              <div
                role="status"
                className="flex flex-wrap items-center justify-between gap-2 rounded-lg bg-warning-soft px-3 py-2 text-sm text-warning-text"
              >
                <span>
                  Ungespeicherte Änderungen. Speichern oder verwerfen, bevor du
                  überarbeitest, zurückholst, kopierst oder übernimmst.
                </span>
                <span className="flex gap-2">
                  <Button size="sm" onClick={save}>
                    Speichern
                  </Button>
                  <Button size="sm" variant="outline" onClick={discard}>
                    Verwerfen
                  </Button>
                </span>
              </div>
            )}

            {!editing && !dirty && compare && (
              <VersionCompare
                draft={draft}
                version={compare}
                onClose={() => setCompare(null)}
                onRestore={async (nr) => setRestoreNr(nr)}
              />
            )}

            <Card>
              <CardContent>
                {editing ? (
                  <div className="flex flex-col gap-3">
                    <Textarea
                      aria-label="Entwurfstext"
                      value={text}
                      onChange={(e) => {
                        setText(e.target.value);
                        setDirty(true);
                      }}
                      rows={30}
                      className="font-mono leading-relaxed"
                    />
                    <div className="flex gap-2">
                      <Button onClick={save}>Speichern</Button>
                      <Button variant="outline" onClick={discard}>
                        Verwerfen
                      </Button>
                    </div>
                  </div>
                ) : (
                  <DraftMarkdown text={text} />
                )}
              </CardContent>
            </Card>

            {assessment && (
              <Card>
                <CardContent>
                  <h3 className="mb-3 text-sm font-semibold tracking-wide text-muted-foreground uppercase">
                    Bewertung
                  </h3>
                  {(draft.aktuelle_nr ?? 1) > 1 && (
                    <p className="mb-3 text-xs text-muted-foreground">
                      Die Bewertung stammt aus dem ersten Lauf und wird bei
                      Überarbeitungen nicht neu berechnet.
                    </p>
                  )}
                  <DraftMarkdown text={assessment} />
                </CardContent>
              </Card>
            )}

            {draft.quellen.length > 0 && (
              <Card>
                <CardContent>
                  <h3 className="mb-2 text-sm font-semibold">
                    Verwendete Quellen ({draft.quellen.length})
                  </h3>
                  <ul className="flex flex-col gap-1">
                    {draft.quellen.map((q, i) => (
                      <li key={i} className="text-sm">
                        {q.url ? (
                          <a
                            href={q.url}
                            target="_blank"
                            rel="noreferrer"
                            className="text-primary hover:underline"
                          >
                            {sourceText(q)}
                          </a>
                        ) : (
                          sourceText(q)
                        )}
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            )}

            <p className="text-xs text-muted-foreground">
              Vor der Einreichung prüfen: Zahlen, Drucksachennummern und die mit
              [ZU PRÜFEN] markierten Stellen.
            </p>
          </div>
        </div>
      )}

      {restoreNr !== null && (
        <RestoreDialog
          nr={restoreNr}
          currentNr={draft.aktuelle_nr}
          onCancel={() => setRestoreNr(null)}
          onConfirm={async () => {
            await restore(restoreNr);
            setRestoreNr(null);
          }}
        />
      )}
    </section>
  );
};
